import { Box, Divider, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { setFarmDevice } from '../../../../../actions/farmSectorActions';
import CustomButton from '../../../../../components/General/CustomButton';
import { UserRole, UserType } from '../../../../../constants/user_types';
import { ReduxState } from '../../../../../types';
import useStyles from '../../styles';
import ListLayout from '../ListLayout';
import ListTile from '../ListTile';
import AddDevicesFlow from './AddDevicesFlow';
import useNodes from '../../../../../hooks/useNodes';
import { filteredDevicesBySector, filteredGatewayNodes } from '../../helpers';
import useSensorModels from '../../../../../hooks/useSensorModels';
import { capitalizeFirstLetter } from '../../../../../utils/helpers';

const DevicesList = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [openAddDevicesModal, setOpenAddDevicesModal] = useState(false);
  const { selectedFarm } = useSelector((state: ReduxState) => state.accountFarm);
  const { selectedSector } = useSelector((state: ReduxState) => state.currentSector);
  const currentUser = useSelector((state: ReduxState) => state.account.user);
  const { nodes } = useNodes(selectedFarm?.id ?? '');
  const { sensorModels } = useSensorModels(selectedFarm?.id ?? '');
  const allFarmDevices = [...nodes, ...sensorModels];
  const isAdmin = currentUser?.role === UserRole.ADMIN && currentUser?.type === UserType.CDTEC;
  const isOperator = currentUser?.role === UserRole.OPERATOR
    && currentUser?.type === UserType.CDTEC;

  const canCreate = isAdmin || isOperator;

  const {
    selectedDevice,
    sensorNodeFilter,
  } = useSelector((state: ReduxState) => state.farmSector);

  const history = useHistory();

  const handleDeviceClick = (deviceId: string) => {
    if (selectedDevice === deviceId) {
      dispatch(setFarmDevice(''));
    } else {
      dispatch(setFarmDevice(deviceId));
    }
  };

  return (
    <>
      <ListLayout>
        <Typography
          variant="h3"
          color="primary"
          sx={{ mb: 3 }}
        >
          Equipos
        </Typography>
        <Divider className={classes.divider} />
        {selectedFarm
          && <>
            {canCreate && (
              <Box marginBottom='12px' marginTop="12px" width='100%'>
                <CustomButton buttonText='AGREGAR EQUIPO' onClick={() => setOpenAddDevicesModal(true)} color='blue' />
              </Box>
            )}
            <Box className={classes.listTilesContainer}>
              {sensorNodeFilter.deviceType === 'node'
                && (nodes || []).filter(
                  (node: any) => filteredDevicesBySector(node, selectedSector?.id ?? ''),
                  (node: any) => filteredGatewayNodes(node, sensorNodeFilter.filterValue),
                ).map((node: any) => {
                  const {
                    id, name, brand, isGatewayNode, wiseconnId,
                  } = node;
                  return (
                    <React.Fragment key={id}>
                      <ListTile
                        key={id}
                        title={name}
                        tileInformation={{
                          Marca: brand ?? 'Sin marca',
                          WiseconnId: wiseconnId ?? 'Sin Wiseconn ID',
                          Gateaway: isGatewayNode ? 'Si' : 'No',
                        }}
                        isActive={selectedDevice === id}
                        onClick={() => handleDeviceClick(id ?? '')}
                        customContainerClassName={classes.actionableListTileContainer}
                        seeMore={() => history.push(`/layout/nodos/${id}`)}
                      />
                      <Divider />
                    </React.Fragment>
                  );
                })}
              {sensorNodeFilter.deviceType === 'sensor'
                && (sensorModels || []).filter(
                  (sensor: any) => filteredDevicesBySector(sensor, selectedSector?.id ?? ''),
                ).map((sensor: any) => {
                  const {
                    _id: id, name, isWeatherStation, zoneId, type,
                  } = sensor;
                  return (
                    <React.Fragment key={id}>
                      <ListTile
                        key={id}
                        title={name}
                        tileInformation={{
                          'Tipo de sensor': capitalizeFirstLetter(type) ?? 'Sin tipo de sensor',
                          'Estación de Clima': isWeatherStation ? 'Si' : 'No',
                          'Zone ID': zoneId ?? 'Sin Zone ID',
                        }}
                        isActive={selectedDevice === id}
                        onClick={() => handleDeviceClick(id ?? '')}
                        customContainerClassName={classes.actionableListTileContainer}
                        seeMore={() => history.push(`/layout/sensorModels/${id}`)}
                      />
                      <Divider />
                    </React.Fragment>
                  );
                })}
              {(selectedFarm && !(allFarmDevices && allFarmDevices.length > 0))
                && <Typography variant='body2'>
                  No hay equipos asociados a este campo
                </Typography>}
            </Box>
          </>}
        {!selectedFarm && <Typography variant='body2'>
          Elige un campo para ver los equipos asociados
        </Typography>}

      </ListLayout>
      <AddDevicesFlow
        isModalOpen={openAddDevicesModal}
        setModalOpen={setOpenAddDevicesModal}
      />
    </>

  );
};

export default DevicesList;
